// frontend/src/components/Requisitions/RequisitionItems.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import apiClient from '../../api/apiClient';
import { useToast } from '../../contexts/ToastContext';
import NoTranslateText from '../UI/NoTranslateText';
import SearchableSelect from './SearchableSelect';

/** ✅ Alert reusable: solo para modo manual (NO APLICA) */
function ManualUomWarning({ unitLabel }) {
  return (
    <div
      style={{
        background: '#FFF3CD',
        border: '1px solid #FFEEBA',
        color: '#856404',
        padding: '10px 12px',
        borderRadius: 10,
        marginBottom: 12,
        lineHeight: 1.35,
      }}
    >
      <div style={{ fontWeight: 700, marginBottom: 4 }}>
        ⚠️ Importante (Licitación: NO APLICA)
      </div>
      <div>
        La <b>descripción</b> que captures debe <b>coincidir con la Unidad de Medida</b>{' '}
        (cuando aplique). Si cambias la unidad, revisa y ajusta la descripción para que
        sea consistente.
      </div>

      {unitLabel ? (
        <div style={{ marginTop: 6, fontSize: 13 }}>
          Unidad seleccionada: <b>{unitLabel}</b>
        </div>
      ) : null}
    </div>
  );
}

/* ============================
   ✅ Helpers
   ============================ */
const getResultsFromPayload = (payload) => {
  if (Array.isArray(payload)) {
    return { results: payload, next: null };
  }
  if (payload && Array.isArray(payload.results)) {
    return {
      results: payload.results,
      next: payload.next || null,
    };
  }
  return { results: [], next: null };
};

const isCatalogItemActive = (item) => {
  if (!item || typeof item !== 'object') return false;
  if (!Object.prototype.hasOwnProperty.call(item, 'active')) return true;

  const value = item.active;
  if (typeof value === 'string') {
    return !['false', '0', 'no', 'off', 'inactive'].includes(value.trim().toLowerCase());
  }
  return Boolean(value);
};

const extractPrimitiveId = (value) => {
  if (value === null || typeof value === 'undefined') return '';
  if (typeof value === 'object') {
    return String(value.id ?? value.pk ?? value.uuid ?? '');
  }
  return String(value);
};

const normalizeId = (value) => {
  const raw = value && typeof value === 'object' ? value.id ?? value.pk ?? value.uuid : value;
  const n = Number(raw);
  return Number.isFinite(n) && n > 0 ? n : null;
};

const normalizeSearchText = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();

const getLockedProductIdFromItems = (items = []) => {
  for (const item of items) {
    const productId = normalizeId(item?.product);
    if (productId) return productId;
  }
  return null;
};

const extractTenderId = (formData) => {
  const candidates = [
    formData?.tender_id,
    formData?.tender?.id,
    formData?.tender?.pk,
    formData?.tender,
  ];

  for (const candidate of candidates) {
    if (candidate === null || typeof candidate === 'undefined') continue;

    if (typeof candidate === 'number' && Number.isFinite(candidate)) {
      return String(candidate);
    }

    if (typeof candidate === 'string') {
      const trimmed = candidate.trim();
      if (/^\d+$/.test(trimmed)) return trimmed;
    }

    if (typeof candidate === 'object') {
      const raw = extractPrimitiveId(candidate);
      if (/^\d+$/.test(raw)) return raw;
    }
  }

  return '';
};

const labelOf = (obj) => {
  if (obj === null || typeof obj === 'undefined') return '';
  if (typeof obj === 'string' || typeof obj === 'number') return String(obj);

  const code = obj?.code || obj?.clave || obj?.codigo;
  const name =
    obj?.name ||
    obj?.nombre ||
    obj?.description ||
    obj?.descripcion ||
    obj?.label ||
    obj?.text ||
    obj?.unit_display ||
    '';

  return code ? `${code} - ${name ?? ''}`.trim() : (name || String(obj?.id ?? ''));
};

const EXPLICIT_AGREEMENT_FUNDING_CODES = ['16000', '16001'];
const NO_APLICA_AGREEMENT_LABEL = '0000 - NO APLICA';

const fetchAllPagesFromUrl = async (url) => {
  let nextUrl = url;
  const rows = [];

  while (nextUrl) {
    const res = await apiClient.get(nextUrl);
    const { results, next } = getResultsFromPayload(res.data);
    rows.push(...results);
    nextUrl = next;
  }

  return rows;
};

const fetchFirstWorkingEndpointAllPages = async (urls) => {
  let lastError = null;

  for (const url of urls) {
    try {
      const rows = await fetchAllPagesFromUrl(url);
      const visibleRows = (rows || []).filter(isCatalogItemActive);
      return visibleRows;
    } catch (err) {
      lastError = err;
    }
  }

  if (lastError) throw lastError;
  return [];
};

const normalizeProductRow = (p) => {
  const id = extractPrimitiveId(p?.id ?? p?.pk ?? p?.uuid);
  const label =
    p?.label ||
    p?.product_description ||
    p?.display ||
    labelOf(p);

  return { id, label };
};

const normalizeUnitRow = (u) => {
  const id = extractPrimitiveId(u?.id ?? u?.pk ?? u?.uuid);
  const label =
    u?.unit_display ||
    u?.label ||
    u?.display ||
    labelOf(u);

  return { id, label };
};

const normalizeDescriptionRow = (d) => {
  const id = extractPrimitiveId(d?.id ?? d?.pk ?? d?.uuid);
  const productId = extractPrimitiveId(
    d?.product ?? d?.product_id ?? d?.producto ?? d?.producto_id
  );
  const tenderId = extractPrimitiveId(
    d?.tender ?? d?.tender_id ?? d?.licitacion ?? d?.licitacion_id
  );
  const unitId = extractPrimitiveId(
    d?.unit ?? d?.unit_id ?? d?.unidad ?? d?.unidad_id
  );

  const unitLabel =
    d?.unit_display ||
    d?.unit_name ||
    d?.unit_label ||
    (typeof d?.unit === 'object' ? labelOf(d.unit) : '') ||
    '';

  const productLabel =
    d?.product_description ||
    d?.product_label ||
    (typeof d?.product === 'object' ? labelOf(d.product) : '');

  return {
    id,
    productId,
    productLabel,
    tenderId,
    text: d?.text ?? d?.descripcion ?? d?.name ?? d?.label ?? '',
    estimated_unit_cost: d?.estimated_unit_cost ?? d?.costo ?? d?.cost ?? '',
    unitId,
    unitLabel,
  };
};

const normalizeHistoryRow = (row) => {
  const quantity =
    row?.quantity === null || typeof row?.quantity === 'undefined'
      ? ''
      : String(row.quantity);

  const estimatedUnitCost =
    row?.estimated_unit_cost === null || typeof row?.estimated_unit_cost === 'undefined'
      ? ''
      : String(row.estimated_unit_cost);

  const estimatedTotal =
    row?.estimated_total === null || typeof row?.estimated_total === 'undefined'
      ? ''
      : String(row.estimated_total);

  return {
    manual_description: String(row?.manual_description || '').trim(),
    quantity,
    unit: row?.unit ? String(row.unit) : '',
    unit_display: String(row?.unit_display || '').trim(),
    estimated_unit_cost: estimatedUnitCost,
    estimated_total: estimatedTotal,
    source_requisition_id: row?.source_requisition_id ?? null,
    source_requisition_folio: row?.source_requisition_folio ?? null,
    source_created_at: row?.source_created_at || null,
  };
};

export default function RequisitionItems({
  formData,
  items,
  setItems,
  requisitionNumber,
  setRequisitionNumber,

  // ✅ modo manual si tender = "- NO APLICA"
  manualItemsMode = false,

  // ✅ viene del padre (create wizard)
  ackCostRealistic = false,
  setAckCostRealistic = () => {},
  showAckCostRealistic = true,
}) {
  const { showToast } = useToast();

  const fmtMoney = (v) => {
    if (v === null || typeof v === 'undefined' || v === '') return '—';
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return n.toFixed(2);
  };

  const fmtMoneyLocale = (v) => {
    if (v === null || typeof v === 'undefined' || v === '') return '—';
    const n = Number(v);
    if (!Number.isFinite(n)) return String(v);
    return n.toLocaleString('es-MX', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const computeTotal = (qtyStr, unitStr) => {
    const qty = Number(qtyStr);
    const unit = Number(unitStr);

    if (Number.isFinite(qty) && qty > 0 && Number.isFinite(unit) && unit > 0) {
      return (qty * unit).toFixed(2);
    }
    return '';
  };

  const buildEmptyItem = (productId = '', productLabel = '') => ({
    product: productId ? String(productId) : '',
    product_label: productLabel || '',
    quantity: '',
    unit: '',
    unit_label: '',
    description: '',
    description_label: '',
    manual_description: '',
    estimated_unit_cost: '',
    estimated_total: '',
  });

  const tenderId = useMemo(() => extractTenderId(formData), [formData]);

  const agreementSummaryLabel = useMemo(() => {
    if (formData?.agreement_label) return formData.agreement_label;
    if (formData?.agreement) return formData.agreement;

    const fundingLabel = String(formData?.funding_source_label || '').trim();
    const hasFundingSource = Boolean(formData?.funding_source);
    const requiresExplicitAgreement = EXPLICIT_AGREEMENT_FUNDING_CODES.some(
      (code) =>
        fundingLabel.startsWith(`${code} `) ||
        fundingLabel.startsWith(`${code}-`) ||
        fundingLabel.startsWith(`${code} -`)
    );

    if (!hasFundingSource || requiresExplicitAgreement) return '';
    return NO_APLICA_AGREEMENT_LABEL;
  }, [
    formData?.agreement,
    formData?.agreement_label,
    formData?.funding_source,
    formData?.funding_source_label,
  ]);

  // ============================
  // STEP 2: Registro de Partidas
  // ============================
  const [products, setProducts] = useState([]);
  const [units, setUnits] = useState([]);
  const [descOptions, setDescOptions] = useState([]);
  const [manualHistoryOptions, setManualHistoryOptions] = useState([]);
  const [loadingManualHistory, setLoadingManualHistory] = useState(false);
  const [manualHistoryMenuOpen, setManualHistoryMenuOpen] = useState(false);

  const productsRequestSeq = useRef(0);
  const descriptionsRequestSeq = useRef(0);
  const historyRequestSeq = useRef(0);
  const loadedManualHistoryProductIdRef = useRef('');

  const [newItem, setNewItem] = useState(buildEmptyItem());
  const [editingItemId, setEditingItemId] = useState(null);

  const lockedProductId = useMemo(
    () => getLockedProductIdFromItems(items || []),
    [items]
  );

  const isProductLocked = lockedProductId !== null;

  const productLabelById = useMemo(() => {
    const map = new Map();
    products.forEach((p) => map.set(String(p.id), p.label));
    return map;
  }, [products]);

  const unitLabelById = useMemo(() => {
    const map = new Map();
    units.forEach((u) => map.set(String(u.id), u.label));
    return map;
  }, [units]);

  const lockedProductLabel = useMemo(() => {
    if (!lockedProductId) return '';

    for (const item of items || []) {
      const itemProductId = normalizeId(item?.product);
      if (itemProductId === lockedProductId) {
        return item?.product_label || productLabelById.get(String(lockedProductId)) || '';
      }
    }

    return productLabelById.get(String(lockedProductId)) || '';
  }, [items, lockedProductId, productLabelById]);

  const effectiveProductId = isProductLocked
    ? String(lockedProductId)
    : (newItem.product || '');

  const effectiveProductLabel = isProductLocked
    ? (
        lockedProductLabel ||
        productLabelById.get(String(lockedProductId)) ||
        ''
      )
    : (
        newItem.product_label ||
        productLabelById.get(String(newItem.product || '')) ||
        ''
      );

  const filteredManualHistoryOptions = useMemo(() => {
    const query = normalizeSearchText(newItem.manual_description);
    const base = Array.isArray(manualHistoryOptions) ? manualHistoryOptions : [];
    const filtered = query
      ? base.filter((entry) =>
          normalizeSearchText(entry?.manual_description || '').includes(query)
        )
      : base;

    return filtered.slice(0, 8);
  }, [manualHistoryOptions, newItem.manual_description]);

  const shouldShowManualHistoryDropdown = useMemo(
    () =>
      Boolean(
        manualItemsMode &&
          effectiveProductId &&
          manualHistoryMenuOpen &&
          normalizeSearchText(newItem.manual_description) &&
          filteredManualHistoryOptions.length > 0
      ),
    [
      effectiveProductId,
      filteredManualHistoryOptions.length,
      manualHistoryMenuOpen,
      manualItemsMode,
      newItem.manual_description,
    ]
  );

  const loadManualHistoryForProduct = useCallback(
    async (productId, { force = false } = {}) => {
      const normalizedProductId = productId ? String(productId) : '';

      if (!manualItemsMode || !normalizedProductId) {
        loadedManualHistoryProductIdRef.current = '';
        setManualHistoryOptions([]);
        setLoadingManualHistory(false);
        setManualHistoryMenuOpen(false);
        return;
      }

      if (!force && loadedManualHistoryProductIdRef.current === normalizedProductId) {
        return;
      }

      const requestId = ++historyRequestSeq.current;
      setLoadingManualHistory(true);

      try {
        const response = await apiClient.get(
          `/catalogs/products/${encodeURIComponent(normalizedProductId)}/user-history/`
        );
        const rows = Array.isArray(response?.data) ? response.data : [];

        if (requestId !== historyRequestSeq.current) return;

        loadedManualHistoryProductIdRef.current = normalizedProductId;
        setManualHistoryOptions(
          rows
            .map(normalizeHistoryRow)
            .filter((row) => row.manual_description)
        );
      } catch (error) {
        if (requestId !== historyRequestSeq.current) return;
        loadedManualHistoryProductIdRef.current = '';
        console.error('Error loading manual item history:', error);
        setManualHistoryOptions([]);
        setManualHistoryMenuOpen(false);
      } finally {
        if (requestId === historyRequestSeq.current) {
          setLoadingManualHistory(false);
        }
      }
    },
    [manualItemsMode]
  );

  const ensureManualHistoryLoaded = useCallback(() => {
    if (!manualItemsMode || !effectiveProductId) return;
    void loadManualHistoryForProduct(effectiveProductId);
  }, [effectiveProductId, loadManualHistoryForProduct, manualItemsMode]);

  useEffect(() => {
    if (manualItemsMode) return;
    loadedManualHistoryProductIdRef.current = '';
    setManualHistoryOptions([]);
    setLoadingManualHistory(false);
    setManualHistoryMenuOpen(false);
  }, [manualItemsMode]);

  // ===== Cargar unidades (una vez) =====
  useEffect(() => {
    let cancelled = false;

    const fetchUnits = async () => {
      let lastError = null;

      for (let attempt = 0; attempt < 3 && !cancelled; attempt += 1) {
        try {
          const unitArr = await fetchFirstWorkingEndpointAllPages([
            '/catalogs/units/',
          ]);

          const deduped = new Map();
          unitArr
            .map(normalizeUnitRow)
            .filter((u) => u.id)
            .forEach((u) => {
              deduped.set(String(u.id), u);
            });

          const mappedUnits = Array.from(deduped.values()).sort((a, b) =>
            (a.label || '').localeCompare(b.label || '', 'es')
          );

          if (mappedUnits.length === 0) {
            throw new Error('No active units were returned.');
          }

          if (!cancelled) {
            setUnits(mappedUnits);
          }
          return;
        } catch (err) {
          lastError = err;

          if (attempt < 2) {
            await new Promise((resolve) => window.setTimeout(resolve, (attempt + 1) * 1000));
            continue;
          }
        }
      }

      if (!cancelled) {
        console.error('Error loading units:', lastError);
        showToast?.('Error al cargar unidades de medida.', 'error');
      }
    };

    void fetchUnits();

    return () => {
      cancelled = true;
    };
  }, [showToast]);

  // ===== Cuando cambia tender o modo manual, limpia draft =====
  useEffect(() => {
    setNewItem(buildEmptyItem());
    setEditingItemId(null);
    setDescOptions([]);
  }, [tenderId, manualItemsMode]);

  // ===== Bloqueo de producto basado en partidas ya registradas =====
  useEffect(() => {
    if (!isProductLocked) return;

    const nextProductId = String(lockedProductId);
    const nextProductLabel =
      lockedProductLabel ||
      productLabelById.get(nextProductId) ||
      '';

    setNewItem((prev) => {
      const prevProductId = normalizeId(prev.product);

      if (prevProductId === lockedProductId) {
        return {
          ...prev,
          product: nextProductId,
          product_label: prev.product_label || nextProductLabel,
        };
      }

      return {
        ...prev,
        product: nextProductId,
        product_label: nextProductLabel,
        description: '',
        description_label: '',
        manual_description: '',
        estimated_unit_cost: '',
        estimated_total: '',
        quantity: '',
        unit: '',
        unit_label: '',
      };
    });

    if (manualItemsMode && nextProductId) {
      void loadManualHistoryForProduct(nextProductId, { force: true });
    }
    setManualHistoryMenuOpen(false);
  }, [
    isProductLocked,
    lockedProductId,
    lockedProductLabel,
    productLabelById,
    manualItemsMode,
    loadManualHistoryForProduct,
  ]);

  // ===== Cargar productos dinámicamente =====
  useEffect(() => {
    const requestId = ++productsRequestSeq.current;
    let isCancelled = false;

    const fetchProducts = async () => {
      try {
        let prodArr = [];

        if (manualItemsMode) {
          prodArr = await fetchFirstWorkingEndpointAllPages([
            '/catalogs/products/',
            '/products/',
            '/catalogs/items/',
            '/catalogs/expense-objects/',
          ]);
        } else {
          if (!tenderId) {
            setProducts([]);
            return;
          }

          const encodedTenderId = encodeURIComponent(tenderId);
          prodArr = await fetchFirstWorkingEndpointAllPages([
            `/catalogs/products/?tender=${encodedTenderId}`,
            `/products/?tender=${encodedTenderId}`,
          ]);
        }

        if (isCancelled || requestId !== productsRequestSeq.current) return;

        const deduped = new Map();
        prodArr
          .map(normalizeProductRow)
          .filter((p) => p.id)
          .forEach((p) => {
            deduped.set(String(p.id), p);
          });

        const mappedProducts = Array.from(deduped.values()).sort((a, b) =>
          (a.label || '').localeCompare(b.label || '', 'es')
        );

        setProducts(mappedProducts);
      } catch (err) {
        console.error('Error loading products for tender:', err);
        setProducts([]);
        showToast?.('Error al cargar productos para la licitación.', 'error');
      }
    };

    fetchProducts();

    return () => {
      isCancelled = true;
    };
  }, [manualItemsMode, tenderId, showToast]);

  // ===== Si el producto actual ya no es válido, límpialo =====
  useEffect(() => {
    if (!newItem.product) return;
    if (products.length === 0) return;

    const exists = products.some((p) => String(p.id) === String(newItem.product));
    if (!exists) {
      setNewItem(buildEmptyItem());
      setDescOptions([]);
    }
  }, [products, newItem.product]);

  const loadDescriptionsByProduct = useCallback(
    async (productId) => {
      if (manualItemsMode) {
        setDescOptions([]);
        return;
      }

      if (!productId || !tenderId) {
        setDescOptions([]);
        return;
      }

      const requestId = ++descriptionsRequestSeq.current;

      try {
        const encodedTenderId = encodeURIComponent(tenderId);
        const encodedProductId = encodeURIComponent(productId);

        const rawRows = await fetchFirstWorkingEndpointAllPages([
          `/catalogs/item-descriptions/?tender=${encodedTenderId}&product=${encodedProductId}`,
          `/item-descriptions/?tender=${encodedTenderId}&product=${encodedProductId}`,
          `/catalogs/descriptions/?tender=${encodedTenderId}&product=${encodedProductId}`,
        ]);

        if (requestId !== descriptionsRequestSeq.current) return;

        const deduped = new Map();
        rawRows
          .map(normalizeDescriptionRow)
          .filter((x) => x.id)
          .filter((x) => String(x.productId) === String(productId))
          .filter((x) => String(x.tenderId) === String(tenderId))
          .forEach((x) => {
            deduped.set(String(x.id), x);
          });

        const options = Array.from(deduped.values()).sort((a, b) =>
          (a.text || '').localeCompare(b.text || '', 'es')
        );

        setDescOptions(options);
      } catch (e) {
        console.error('Error loading item-descriptions by tender+product:', e);
        setDescOptions([]);
        showToast?.('Error al cargar descripciones para el producto.', 'error');
      }
    },
    [manualItemsMode, tenderId, showToast]
  );

  // ===== Cargar descripciones cuando cambia producto =====
  useEffect(() => {
    if (manualItemsMode) {
      setDescOptions([]);
      return;
    }

    if (!effectiveProductId) {
      setDescOptions([]);
      return;
    }

    loadDescriptionsByProduct(effectiveProductId);
  }, [manualItemsMode, effectiveProductId, loadDescriptionsByProduct]);

  // ===== Si la descripción seleccionada ya no existe, límpiala =====
  useEffect(() => {
    if (manualItemsMode) return;
    if (!newItem.description) return;
    if (descOptions.length === 0) return;

    const exists = descOptions.some((d) => String(d.id) === String(newItem.description));
    if (!exists) {
      setNewItem((p) => ({
        ...p,
        description: '',
        description_label: '',
        unit: '',
        unit_label: '',
        estimated_unit_cost: '',
        estimated_total: computeTotal(p.quantity, ''),
      }));
    }
  }, [manualItemsMode, descOptions, newItem.description]);

  // ===== Si en manual la unidad ya no existe, límpiala =====
  useEffect(() => {
    if (!manualItemsMode) return;
    if (!newItem.unit) return;
    if (units.length === 0) return;

    const exists = units.some((u) => String(u.id) === String(newItem.unit));
    if (!exists) {
      setNewItem((p) => ({
        ...p,
        unit: '',
        unit_label: '',
      }));
    }
  }, [manualItemsMode, units, newItem.unit]);

  const handleDescriptionChange = (descId) => {
    if (manualItemsMode) return;

    if (!descId) {
      setNewItem((p) => ({
        ...p,
        description: '',
        description_label: '',
        unit: '',
        unit_label: '',
        estimated_unit_cost: '',
        estimated_total: computeTotal(p.quantity, ''),
      }));
      return;
    }

    const selected = descOptions.find((d) => String(d.id) === String(descId));
    const nextCost =
      selected?.estimated_unit_cost !== null &&
      typeof selected?.estimated_unit_cost !== 'undefined' &&
      selected?.estimated_unit_cost !== ''
        ? String(selected.estimated_unit_cost)
        : '';

    const nextUnitId = selected?.unitId ? String(selected.unitId) : '';
    const nextUnitLabel =
      selected?.unitLabel ||
      unitLabelById.get(nextUnitId) ||
      '';

    setNewItem((p) => ({
      ...p,
      description: String(descId),
      description_label: selected?.text || '',
      unit: nextUnitId,
      unit_label: nextUnitLabel,
      estimated_unit_cost: nextCost,
      estimated_total: computeTotal(p.quantity, nextCost),
    }));
  };

  const handleManualHistorySelection = (entry) => {
    if (!entry || !manualItemsMode) return;

    const nextQuantity = entry.quantity ? String(entry.quantity) : '';
    const nextUnitId = entry.unit ? String(entry.unit) : '';
    const nextUnitLabel =
      entry.unit_display ||
      unitLabelById.get(nextUnitId) ||
      '';
    const nextUnitCost =
      entry.estimated_unit_cost !== null &&
      typeof entry.estimated_unit_cost !== 'undefined' &&
      entry.estimated_unit_cost !== ''
        ? String(entry.estimated_unit_cost)
        : '';

    setNewItem((prev) => ({
      ...prev,
      product: effectiveProductId ? String(effectiveProductId) : prev.product,
      product_label: effectiveProductLabel || prev.product_label,
      manual_description: entry.manual_description || '',
      quantity: nextQuantity,
      unit: nextUnitId,
      unit_label: nextUnitLabel,
      estimated_unit_cost: nextUnitCost,
      estimated_total: computeTotal(nextQuantity, nextUnitCost),
      description: '',
      description_label: '',
    }));
    setManualHistoryMenuOpen(false);
  };

  const resetDraftItem = useCallback(() => {
    setEditingItemId(null);
    setNewItem(buildEmptyItem(
      effectiveProductId ? String(effectiveProductId) : '',
      effectiveProductLabel || ''
    ));
  }, [effectiveProductId, effectiveProductLabel]);

  useEffect(() => {
    if (editingItemId === null) return;
    const stillExists = (items || []).some((item) => String(item.id) === String(editingItemId));
    if (!stillExists) {
      resetDraftItem();
    }
  }, [editingItemId, items, resetDraftItem]);

  const saveItem = () => {
    const qty = Number(newItem.quantity);
    const unitCost = Number(newItem.estimated_unit_cost);
    const productIdToUse = effectiveProductId ? String(effectiveProductId) : '';
    const productLabelToUse = effectiveProductLabel || '';

    if (!productIdToUse) {
      return showToast?.('Selecciona el Objeto del Gasto.', 'error');
    }

    if (!Number.isFinite(qty) || qty <= 0) {
      return showToast?.('La cantidad debe ser mayor que 0.', 'error');
    }

    if (manualItemsMode) {
      if (!newItem.manual_description.trim()) {
        return showToast?.('Captura la Descripción (manual).', 'error');
      }

      if (!newItem.unit) {
        return showToast?.('Selecciona la Unidad de Medida.', 'error');
      }

      if (!Number.isFinite(unitCost) || unitCost <= 0) {
        return showToast?.(
          'El costo unitario es obligatorio y debe ser mayor que 0.',
          'error'
        );
      }

      const totalNum = Number((qty * unitCost).toFixed(2));
      if (!Number.isFinite(totalNum) || totalNum <= 0) {
        return showToast?.(
          'No se pudo calcular el total (cantidad × unitario).',
          'error'
        );
      }

      const partida = {
        isTemp: true,
        product: productIdToUse,
        product_label: productLabelToUse,
        quantity: qty,
        unit: newItem.unit,
        unit_label: newItem.unit_label,
        description: null,
        manual_description: newItem.manual_description.trim(),
        description_label: newItem.manual_description.trim(),
        estimated_unit_cost: Number(unitCost.toFixed(2)),
        estimated_total: totalNum,
      };

      setItems((prev) => {
        if (editingItemId === null) {
          return [...prev, { ...partida, id: Date.now() }];
        }

        return prev.map((row) =>
          String(row.id) === String(editingItemId)
            ? { ...row, ...partida, id: row.id, isTemp: row.isTemp ?? true }
            : row
        );
      });

      resetDraftItem();
      return;
    }

    if (!newItem.description) {
      return showToast?.('Selecciona la Descripción.', 'error');
    }

    if (!newItem.unit) {
      return showToast?.(
        'No se pudo resolver la Unidad de Medida desde la descripción.',
        'error'
      );
    }

    if (!Number.isFinite(unitCost) || unitCost <= 0) {
      return showToast?.(
        'El costo unitario (desde la descripción) es obligatorio y debe ser mayor que 0.',
        'error'
      );
    }

    const totalNum = Number((qty * unitCost).toFixed(2));
    if (!Number.isFinite(totalNum) || totalNum <= 0) {
      return showToast?.(
        'No se pudo calcular el total (cantidad × unitario).',
        'error'
      );
    }

    const partida = {
      isTemp: true,
      product: productIdToUse,
      product_label: productLabelToUse,
      quantity: qty,
      unit: newItem.unit,
      unit_label: newItem.unit_label,
      description: newItem.description,
      description_label: newItem.description_label,
      manual_description: null,
      estimated_unit_cost: Number(unitCost.toFixed(2)),
      estimated_total: totalNum,
    };

    setItems((prev) => {
      if (editingItemId === null) {
        return [...prev, { ...partida, id: Date.now() }];
      }

      return prev.map((row) =>
        String(row.id) === String(editingItemId)
          ? { ...row, ...partida, id: row.id, isTemp: row.isTemp ?? true }
          : row
      );
    });

    resetDraftItem();
  };

  const beginEditItem = (item) => {
    const normalizedId = item?.id ?? null;
    if (normalizedId === null) return;

    const productId = normalizeId(item?.product);
    const productLabel =
      item?.product_label ||
      productLabelById.get(String(productId || '')) ||
      '';

    setEditingItemId(normalizedId);
    setNewItem({
      product: productId ? String(productId) : '',
      product_label: productLabel,
      quantity: item?.quantity === null || typeof item?.quantity === 'undefined' ? '' : String(item.quantity),
      unit: item?.unit ? String(item.unit) : '',
      unit_label: item?.unit_label || '',
      description: item?.description ? String(item.description) : '',
      description_label: item?.description_label || '',
      manual_description: item?.manual_description || '',
      estimated_unit_cost:
        item?.estimated_unit_cost === null || typeof item?.estimated_unit_cost === 'undefined'
          ? ''
          : String(item.estimated_unit_cost),
      estimated_total:
        item?.estimated_total === null || typeof item?.estimated_total === 'undefined'
          ? ''
          : String(item.estimated_total),
    });
  };

  const removeItem = (id) => {
    if (String(id) === String(editingItemId)) {
      resetDraftItem();
    }
    setItems((prev) => prev.filter((p) => String(p.id) !== String(id)));
  };

  // ===== Requisition Number (auto) =====
  const tempNumberFetched = useRef(false);

  const extractLastNumber = (payload) => {
    const arr = Array.isArray(payload)
      ? payload
      : (Array.isArray(payload?.results) ? payload.results : []);
    const first = arr[0] || null;
    if (!first) return null;

    const raw =
      first.number ??
      first.folio ??
      first.consecutive ??
      first.no ??
      first.n ??
      first.id ??
      null;

    const parsed = Number(raw);
    return Number.isFinite(parsed) ? parsed : null;
  };

  useEffect(() => {
    const fetchTempNumber = async () => {
      try {
        const urls = [
          '/requisitions/?ordering=-number&limit=1',
          '/requisitions/?ordering=-number&page_size=1',
          '/requisitions/?ordering=-id&limit=1',
          '/requisitions/?ordering=-id&page_size=1',
        ];

        let last = null;

        for (const url of urls) {
          try {
            const res = await apiClient.get(url);
            const candidate = extractLastNumber(res.data);
            if (candidate !== null) {
              last = candidate;
              break;
            }
          } catch {
            /* try next */
          }
        }

        const next = (last ?? 0) + 1;
        setRequisitionNumber(String(next));
      } catch (err) {
        console.error('No se pudo obtener el último número de requisición:', err);
        setRequisitionNumber('1');
      }
    };

    if (!tempNumberFetched.current) {
      tempNumberFetched.current = true;
      fetchTempNumber();
    }
  }, [setRequisitionNumber]);

  // ======= Catálogo modal (solo catálogo) =======
  const [showCatalogModal, setShowCatalogModal] = useState(false);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(null);
  const [catalogRows, setCatalogRows] = useState([]);

  const [entriesPerPage, setEntriesPerPage] = useState(10);
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);

  const openPDF = () => {
    const url = `${process.env.PUBLIC_URL}/docs/clasificador.pdf`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const openCatalog = async () => {
    if (manualItemsMode) return;

    if (!tenderId) {
      showToast?.('No se pudo resolver la licitación seleccionada.', 'error');
      return;
    }

    setShowCatalogModal(true);
    setCatalogError(null);
    setSearchQuery('');
    setEntriesPerPage(10);
    setPage(1);

    try {
      setCatalogLoading(true);

      const encodedTenderId = encodeURIComponent(tenderId);

      const data = await fetchFirstWorkingEndpointAllPages([
        `/catalogs/item-descriptions/?tender=${encodedTenderId}`,
        `/item-descriptions/?tender=${encodedTenderId}`,
        `/catalogs/descriptions/?tender=${encodedTenderId}`,
      ]);

      const normalized = data
        .map(normalizeDescriptionRow)
        .filter((d) => d.id)
        .filter((d) => String(d.tenderId) === String(tenderId))
        .map((d) => ({
          id: d.id,
          productId: d.productId,
          productLabel:
            d.productLabel ||
            productLabelById.get(String(d.productId)) ||
            d.productId ||
            '—',
          text: d.text,
          unitLabel:
            d.unitLabel ||
            unitLabelById.get(String(d.unitId)) ||
            '—',
          cost: d.estimated_unit_cost,
        }));

      setCatalogRows(normalized);
    } catch (e) {
      console.error('openCatalog error:', e);
      setCatalogError('No se pudo cargar el catálogo.');
    } finally {
      setCatalogLoading(false);
    }
  };

  const closeCatalog = () => {
    setShowCatalogModal(false);
    setCatalogRows([]);
    setSearchQuery('');
    setCatalogError(null);
    setCatalogLoading(false);
    setPage(1);
  };

  const filteredRows = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return catalogRows;

    return catalogRows.filter((r) =>
      (r.text || '').toLowerCase().includes(q) ||
      (r.productLabel || '').toLowerCase().includes(q) ||
      (r.unitLabel || '').toLowerCase().includes(q)
    );
  }, [catalogRows, searchQuery]);

  useEffect(() => {
    setPage(1);
  }, [entriesPerPage, searchQuery]);

  const totalEntries = filteredRows.length;
  const totalPages = Math.max(1, Math.ceil(totalEntries / entriesPerPage));
  const currentPage = Math.min(page, totalPages);
  const startIndex = (currentPage - 1) * entriesPerPage;
  const endIndex = Math.min(startIndex + entriesPerPage, totalEntries);
  const pageRows = filteredRows.slice(startIndex, endIndex);

  const goToPage = (p) => {
    if (p < 1 || p > totalPages) return;
    setPage(p);
  };

  const pageList = useMemo(() => {
    const pages = [];
    const N = totalPages;
    const windowSize = 1;

    if (N <= 7) {
      for (let i = 1; i <= N; i++) pages.push(i);
      return pages;
    }

    pages.push(1);
    const left = Math.max(2, currentPage - windowSize);
    const right = Math.min(N - 1, currentPage + windowSize);

    if (left > 2) pages.push('…');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < N - 1) pages.push('…');
    pages.push(N);

    return pages;
  }, [currentPage, totalPages]);

  const baseEnabled = manualItemsMode ? !!effectiveProductId : !!newItem.description;

  const currentUnitLabel = useMemo(() => {
    const id = String(newItem.unit || '');
    if (!id) return '';
    return unitLabelById.get(id) || '';
  }, [newItem.unit, unitLabelById]);

  return (
    <>
      <div className="flex flex-wrap items-center gap-2 mt-2 mb-4">
        <h2 className="text-xl font-semibold">
          Requisicion No. {requisitionNumber || '...'} Información de registro
        </h2>
      </div>

      <h3 className="text-lg font-semibold mb-3">Resumen</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-6">
        <div>
          <div className="text-sm text-gray-500">Departamento</div>
          <div className="font-medium">{formData.department}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Proyecto</div>
          <div className="font-medium">{formData.project_label || formData.project}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Fuente de Financiamiento</div>
          <div className="font-medium">{formData.funding_source_label || formData.funding_source}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Unidad Presupuestal</div>
          <div className="font-medium">{formData.budget_unit_label || formData.budget_unit}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Convenio</div>
          <div className="font-medium">{agreementSummaryLabel}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Licitación</div>
          <div className="font-medium">{formData.tender_label || formData.tender}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Fecha</div>
          <div className="font-medium">{formData.fecha}</div>
        </div>
        <div className="md:col-span-2">
          <div className="text-sm text-gray-500">Motivos Requisición</div>
          <div className="font-medium">{formData.description}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Solicitante</div>
          <div className="font-medium">{formData.solicitante}</div>
        </div>
        <div>
          <div className="text-sm text-gray-500">Servicio Externo / Académico</div>
          <div className="font-medium">{formData.external_service_label || formData.external_service}</div>
        </div>
      </div>

      <div className="my-6 border-t border-gray-200" />

      <h3 className="text-lg font-semibold mb-2">Registro de Partidas</h3>

      {manualItemsMode ? <ManualUomWarning unitLabel={currentUnitLabel} /> : null}

      <div className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end mb-3">
        {/* Objeto del Gasto */}
        <div className="md:col-span-4">
          <label className="block mb-1 font-medium">Objeto del Gasto</label>
          <select
            value={effectiveProductId}
            onChange={(e) => {
              if (isProductLocked) return;

              const id = e.target.value;
              const label = e.target.options[e.target.selectedIndex]?.text || '';

              setNewItem((p) => ({
                ...p,
                product: id,
                product_label: label,
                description: '',
                description_label: '',
                manual_description: '',
                estimated_unit_cost: '',
                estimated_total: '',
                quantity: '',
                unit: '',
                unit_label: '',
              }));

              loadedManualHistoryProductIdRef.current = '';
              setManualHistoryOptions([]);

              if (manualItemsMode && id) {
                void loadManualHistoryForProduct(id, { force: true });
              } else {
                setLoadingManualHistory(false);
              }
            }}
            className="border p-2 w-full rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
            disabled={isProductLocked || (!manualItemsMode && !tenderId)}
          >
            <option value="">
              {!manualItemsMode && !tenderId
                ? 'Seleccione primero una Licitación'
                : 'Seleccione Objeto del Gasto'}
            </option>
            {products.map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
          </select>

          {isProductLocked && (
            <div className="text-[11px] text-gray-500 mt-1">
              El objeto del gasto quedó fijado por la primera partida de esta requisición.
            </div>
          )}
        </div>

        {/* Descripción */}
        <div className="md:col-span-8">
          <label className="block mb-1 font-medium">Descripción</label>

          {manualItemsMode ? (
            <>
            <input
              value={newItem.manual_description}
              onChange={(e) => {
                const nextValue = e.target.value;
                setNewItem((p) => ({ ...p, manual_description: nextValue }));
                ensureManualHistoryLoaded();
                setManualHistoryMenuOpen(Boolean(normalizeSearchText(nextValue)));
              }}
              onFocus={() => {
                ensureManualHistoryLoaded();
                setManualHistoryMenuOpen(Boolean(normalizeSearchText(newItem.manual_description)));
              }}
              onBlur={() => {
                window.setTimeout(() => {
                  setManualHistoryMenuOpen(false);
                }, 120);
              }}
              disabled={!effectiveProductId}
              className="border p-2 w-full rounded disabled:bg-gray-100 disabled:cursor-not-allowed notranslate"
              translate="no"
              placeholder={
                effectiveProductId
                  ? 'Escribe la descripción (manual)'
                  : 'Selecciona primero un Producto'
              }
            />
            {loadingManualHistory && effectiveProductId ? (
              <div className="mt-1 text-[11px] text-gray-500">Cargando historial...</div>
            ) : null}
            {shouldShowManualHistoryDropdown ? (
              <div className="mt-1 rounded-md border border-slate-200 bg-white shadow-lg">
                <div className="max-h-48 overflow-y-auto py-1">
                  {filteredManualHistoryOptions.map((entry) => {
                    const key = [
                      entry.manual_description || '',
                      entry.unit || '',
                      entry.estimated_unit_cost || '',
                      entry.source_requisition_id || '',
                    ].join('|');

                    return (
                      <button
                        key={key}
                        type="button"
                        className="block w-full px-3 py-2 text-left hover:bg-slate-50 focus:bg-slate-50"
                        onMouseDown={(event) => {
                          event.preventDefault();
                          handleManualHistorySelection(entry);
                        }}
                      >
                        <NoTranslateText
                          className="font-medium text-sm text-slate-900 block"
                          text={entry.manual_description}
                        />
                        <div className="mt-1 text-[11px] text-slate-500">
                          Cantidad {entry.quantity || '—'} · {entry.unit_display || 'Sin unidad'} · $
                          {fmtMoney(entry.estimated_unit_cost)}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ) : null}
            </>
          ) : (
            <SearchableSelect
              value={newItem.description}
              onChange={handleDescriptionChange}
              disabled={!effectiveProductId}
              placeholder={
                effectiveProductId
                  ? 'Seleccione Descripción'
                  : 'Seleccione primero un Producto'
              }
              options={descOptions.map((d) => ({
                value: d.id,
                label: `${d.text}${d.unitLabel ? ` — ${d.unitLabel}` : ''}${
                  d.estimated_unit_cost ? ` — $${fmtMoney(d.estimated_unit_cost)}` : ''
                }`,
                search: d.text,
              }))}
            />
          )}
        </div>

        {/* Cantidad */}
        <div className="md:col-span-3">
          <label className="block mb-1 font-medium">Cantidad</label>
          <input
            type="number"
            min="1"
            step="1"
            value={newItem.quantity}
            onChange={(e) => {
              const val = e.target.value;
              setNewItem((p) => ({
                ...p,
                quantity: val,
                estimated_total: computeTotal(val, p.estimated_unit_cost),
              }));
            }}
            disabled={!baseEnabled}
            className="border p-2 w-full rounded disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
        </div>

        {/* Unidad */}
        <div className="md:col-span-3">
          <label className="block mb-1 font-medium">Unidad de Medida</label>
          <select
            value={newItem.unit}
            onChange={(e) => {
              if (!manualItemsMode) return;
              const id = e.target.value;
              const label = e.target.options[e.target.selectedIndex]?.text || '';
              setNewItem((p) => ({
                ...p,
                unit: id,
                unit_label: label,
              }));
            }}
            disabled={!manualItemsMode || !effectiveProductId}
            className={`border p-2 w-full rounded ${
              manualItemsMode ? '' : 'bg-gray-100'
            } disabled:cursor-not-allowed`}
          >
            <option value="">Seleccione Unidad</option>
            {units.map((u) => (
              <option key={u.id} value={u.id}>
                {u.label}
              </option>
            ))}
          </select>
          <div className="text-[11px] text-gray-500 mt-1">
            {manualItemsMode
              ? 'Selecciona manualmente la unidad.'
              : 'Se llena automáticamente desde la descripción.'}
          </div>
        </div>

        {/* Costo unitario */}
        <div className="md:col-span-3">
          <label className="block mb-1 font-medium">Costo unitario</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={newItem.estimated_unit_cost}
            readOnly={!manualItemsMode}
            disabled={!baseEnabled}
            onChange={(e) => {
              if (!manualItemsMode) return;
              const val = e.target.value;
              setNewItem((p) => ({
                ...p,
                estimated_unit_cost: val,
                estimated_total: computeTotal(p.quantity, val),
              }));
            }}
            className={`border p-2 w-full rounded ${
              manualItemsMode ? '' : 'bg-gray-100'
            }`}
            placeholder="0.00"
          />
          <div className="text-[11px] text-gray-500 mt-1">
            {manualItemsMode
              ? 'Captura manualmente el costo unitario.'
              : 'Se llena automáticamente desde la descripción.'}
          </div>
        </div>

        {/* Total (READONLY) */}
        <div className="md:col-span-3">
          <label className="block mb-1 font-medium">Monto estimado (total)</label>
          <input
            type="number"
            min="0"
            step="0.01"
            value={newItem.estimated_total}
            readOnly
            className="border p-2 w-full rounded bg-gray-100"
            placeholder="0.00"
          />
          <div className="text-[11px] text-gray-500 mt-1">
            Se calcula automáticamente (cantidad × unitario).
          </div>
        </div>
      </div>

      <div className="flex items-center justify-end mb-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={openPDF}
            className="inline-flex items-center gap-2 rounded-md border border-indigo-300 px-3 py-1.5 text-sm font-medium text-indigo-700 hover:bg-indigo-50"
          >
            Clasificador
          </button>

          {!manualItemsMode && (
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openCatalog();
              }}
              className="inline-flex items-center gap-2 rounded-md border border-blue-300 px-3 py-1.5 text-sm font-medium text-blue-700 hover:bg-blue-50"
            >
              Ver Catálogo
            </button>
          )}

          <button
            type="button"
            onClick={saveItem}
            className="inline-flex items-center gap-2 rounded-md border border-green-300 px-3 py-1.5 text-sm font-medium text-green-700 hover:bg-green-50"
          >
            {editingItemId === null ? 'Agregar' : 'Guardar cambios'}
          </button>

          {editingItemId !== null && (
            <button
              type="button"
              onClick={resetDraftItem}
              className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
            >
              Cancelar edicion
            </button>
          )}
        </div>
      </div>

      <div className="my-4 border-t border-gray-200" />

      <h2 className="text-lg font-semibold mb-3">Partidas registradas</h2>

      <div className="overflow-x-auto">
        <table className="w-full text-sm border rounded">
          <thead>
            <tr className="bg-gray-100 text-left">
              <th className="p-2 border">#</th>
              <th className="p-2 border">Objeto del Gasto</th>
              <th className="p-2 border">Cantidad</th>
              <th className="p-2 border">Unidad</th>
              <th className="p-2 border">Unitario</th>
              <th className="p-2 border">Total</th>
              <th className="p-2 border">Descripción</th>
              <th className="p-2 border">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr>
                <td colSpan="8" className="p-3 text-center text-gray-500">
                  No hay partidas registradas.
                </td>
              </tr>
            )}

            {items.map((p, idx) => (
              <tr key={p.id}>
                <td className="p-2 border">{idx + 1}</td>
                <td className="p-2 border">{p.product_label}</td>
                <td className="p-2 border">{p.quantity}</td>
                <td className="p-2 border">{p.unit_label}</td>
                <td className="p-2 border">
                  {p.estimated_unit_cost ? fmtMoneyLocale(p.estimated_unit_cost) : '—'}
                </td>
                <td className="p-2 border">{fmtMoneyLocale(p.estimated_total)}</td>
                <td className="p-2 border">
                  <NoTranslateText text={p.description_label || p.manual_description || '—'} />
                </td>
                <td className="p-2 border">
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => beginEditItem(p)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                    >
                      Editar
                    </button>
                    <button
                      type="button"
                      onClick={() => removeItem(p.id)}
                      className="bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                    >
                      Eliminar
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAckCostRealistic && (
        <div className="mt-6 border rounded p-3 bg-gray-50">
          <label className="flex items-start gap-2">
            <input
              type="checkbox"
              checked={!!ackCostRealistic}
              onChange={(e) => setAckCostRealistic(e.target.checked)}
            />
            <span className="text-sm">
              Confirma <strong>“costo aproximado realista”</strong> para imprimir/exportar PDF.
            </span>
          </label>
        </div>
      )}

      {/* ============================
          Modal: Ver Catálogo
         ============================ */}
      {showCatalogModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-6xl rounded-lg bg-white shadow-lg">
            <div className="flex items-center justify-between border-b px-4 py-3">
              <h3 className="text-lg font-semibold">
                Catálogo de descripciones activas
              </h3>
              <button
                type="button"
                onClick={closeCatalog}
                className="rounded border px-3 py-1 text-sm hover:bg-gray-50"
              >
                Cerrar
              </button>
            </div>

            <div className="p-4">
              <div className="mb-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Buscar por descripción, objeto del gasto o unidad..."
                  className="w-full md:max-w-md rounded border p-2"
                />

                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600">Mostrar</label>
                  <select
                    value={entriesPerPage}
                    onChange={(e) => setEntriesPerPage(Number(e.target.value))}
                    className="rounded border p-2"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                    <option value={100}>100</option>
                  </select>
                  <span className="text-sm text-gray-600">registros</span>
                </div>
              </div>

              {catalogLoading ? (
                <div className="py-8 text-center text-gray-500">
                  Cargando catálogo...
                </div>
              ) : catalogError ? (
                <div className="rounded border border-red-200 bg-red-50 p-3 text-red-700">
                  {catalogError}
                </div>
              ) : (
                <>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm border rounded">
                      <thead>
                        <tr className="bg-gray-100 text-left">
                          <th className="p-2 border">Objeto del Gasto</th>
                          <th className="p-2 border">Descripción</th>
                          <th className="p-2 border">Unidad</th>
                          <th className="p-2 border">Costo unitario</th>
                        </tr>
                      </thead>
                      <tbody>
                        {pageRows.length === 0 ? (
                          <tr>
                            <td colSpan="4" className="p-4 text-center text-gray-500">
                              No hay resultados.
                            </td>
                          </tr>
                        ) : (
                          pageRows.map((row) => (
                            <tr key={row.id}>
                              <td className="p-2 border">{row.productLabel || '—'}</td>
                              <td className="p-2 border">{row.text || '—'}</td>
                              <td className="p-2 border">{row.unitLabel || '—'}</td>
                              <td className="p-2 border">{fmtMoneyLocale(row.cost)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </table>
                  </div>

                  <div className="mt-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                    <div className="text-sm text-gray-600">
                      Mostrando {totalEntries === 0 ? 0 : startIndex + 1} a {endIndex} de{' '}
                      {totalEntries} registros
                    </div>

                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage <= 1}
                        className="rounded border px-3 py-1 text-sm disabled:opacity-50"
                      >
                        Anterior
                      </button>

                      {pageList.map((p, idx) =>
                        p === '…' ? (
                          <span key={`dots-${idx}`} className="px-2 text-sm text-gray-500">
                            …
                          </span>
                        ) : (
                          <button
                            key={p}
                            type="button"
                            onClick={() => goToPage(p)}
                            className={`rounded border px-3 py-1 text-sm ${
                              p === currentPage ? 'bg-blue-600 text-white border-blue-600' : ''
                            }`}
                          >
                            {p}
                          </button>
                        )
                      )}

                      <button
                        type="button"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage >= totalPages}
                        className="rounded border px-3 py-1 text-sm disabled:opacity-50"
                      >
                        Siguiente
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
